/**
 * The evidence card, and turning the claims on it into findings.
 *
 * One subject in two halves. The card lists what an answer asserted, each claim marked
 * with its epistemic status and the verses it rests on; SaveFinding is what happens when
 * the reader decides some of them are worth keeping.
 *
 * Curation happens BEFORE storage, and that is the whole design of this file. The
 * checkboxes are not a convenience — saving everything and pruning later fills a corpus
 * with claims the reader never thought were findings, and the graph is only worth having
 * if every node was chosen. The same reasoning puts the consent dialog here: the Article 9
 * moment is the first thing that would ever be stored, not registration, and declining
 * falls back to the download, which is the same file. Saying no costs the reader nothing.
 *
 * Split from cards.tsx, which keeps the output window and the reading cards. The `Card`
 * union stays there and is imported here as a type only, so the runtime dependency keeps
 * pointing one way.
 */

import type { JSX } from 'react'

import { useState } from 'react'
import type { Account } from './account'
import { saveFinding } from './account'
import { ConsentDialog } from './accountUi'
import type { AnswerSummary, Claim } from './api'
import type { Card } from './cards'
import { downloadFinding } from './finding'
import { t } from './i18n'


export function EvidenceCard({
  question,
  prose,
  summary,
  account,
  onAccountChange,
  onOpen,
}: {
  question: string
  prose: string
  summary: AnswerSummary
  account: Account | null
  onAccountChange: (account: Account | null) => void
  onOpen: (card: Card) => void
}): JSX.Element {
  const save = (
    <SaveFinding
      question={question}
      prose={prose}
      summary={summary}
      account={account}
      onAccountChange={onAccountChange}
    />
  )

  if (summary.claims.length === 0) {
    return (
      <article>
        <h2 className="card__kind">{t('card.evidence')}</h2>
        <p className="muted">{t('evidence.none')}</p>
        {save}
      </article>
    )
  }

  return (
    <article>
      <h2 className="card__kind">{t('card.evidence')}</h2>
      <ol className="claims">
        {summary.claims.map((claim, index) => (
          <ClaimRow key={index} claim={claim} onOpen={onOpen} />
        ))}
      </ol>
      {save}
    </article>
  )
}

function ClaimRow({ claim, onOpen }: { claim: Claim; onOpen: (card: Card) => void }) {
  return (
    <li className={`claim claim--${claim.status}`}>
      <p className="claim__text">{claim.claim}</p>
      <p className="claim__meta">
        <span className={`badge badge--${claim.status}`}>
          {t(`evidence.status.${claim.status}` as never)}
        </span>
        <span className="muted">{t(`evidence.basis.${claim.basis}` as never)}</span>
        {claim.tier !== null && (
          <span className="tier">{t('tier.label', { ordinal: claim.tier })}</span>
        )}
      </p>
      {claim.references.length > 0 && (
        <p className="claim__refs">
          {t('evidence.references')}:{' '}
          {claim.references.map((reference) => (
            <button
              key={reference}
              type="button"
              className="link"
              onClick={() => onOpen({ kind: 'passage', reference })}
            >
              {reference}
            </button>
          ))}
        </p>
      )}
      {claim.versions.length > 0 && (
        <p className="claim__refs muted">
          {t('evidence.versions')}: {claim.versions.join(', ')}
        </p>
      )}
    </li>
  )
}


/**
 * The save control: a download for anyone, a stored finding for an account.
 *
 * The consent dialog appears exactly once, before the first thing that would
 * ever be stored. Declining is not a dead end — it falls back to the download,
 * which is the same file — so a reader who would rather keep their study on
 * their own disk loses nothing by saying no.
 */
/**
 * Choosing which of an answer's claims are worth keeping.
 *
 * Curation happens before storage, not after — so this panel stands between the answer and
 * the first row written, and "download instead" is always on it. A reader who would rather
 * keep their study on their own disk must never have to consent to get the same file.
 */
function CurationPanel({
  claims,
  kept,
  onKept,
  onKeep,
  onCancel,
  onDownload,
}: {
  claims: AnswerSummary['claims']
  kept: number[]
  onKept: (kept: number[]) => void
  onKeep: () => void
  onCancel: () => void
  onDownload: () => void
}): JSX.Element {
  return (
    <section className="curation">
      <h4>{t('curation.title')}</h4>
      <p className="muted">{t('curation.note')}</p>
      {claims.map((claim, index) => (
        <label key={index} className="picker__row">
          <input
            type="checkbox"
            checked={kept.includes(index)}
            onChange={(event) =>
              onKept(
                event.target.checked
                  ? [...kept, index]
                  : kept.filter((value) => value !== index),
              )
            }
          />
          <span>{claim.claim}</span>
        </label>
      ))}
      <div className="form__row">
        <button type="button" disabled={kept.length === 0} onClick={onKeep}>
          {t('curation.keep', { count: kept.length })}
        </button>
        <button type="button" className="link" onClick={onCancel}>
          {t('editor.cancel')}
        </button>
        <button type="button" className="link" onClick={onDownload}>
          {t('curation.downloadInstead')}
        </button>
      </div>
    </section>
  )
}

/** Everything the save flow is doing, and the three ways a reader moves it along. */
interface Curation {
  asking: boolean
  curating: boolean
  kept: number[]
  setKept: (kept: number[]) => void
  saved: number
  failure: string | null
  download: () => void
  begin: () => void
  keep: () => void
  cancel: () => void
  agree: () => void
  decline: () => void
}

/**
 * Write the chosen claims, and report how many became findings.
 *
 * Outside the hook because it is the one part that talks to the server: the whole request
 * body is assembled here, so the shape the API is promised lives in one place rather than
 * inside a state setter.
 */
async function keepClaims({
  question,
  prose,
  summary,
  selected,
  consent,
}: {
  question: string
  prose: string
  summary: AnswerSummary
  selected: number[]
  consent: boolean
}): Promise<number> {
  const findings = await saveFinding({
    corpus: summary.corpus,
    question,
    prose,
    crossed_a_border: summary.crossed_a_border,
    claims: summary.claims,
    selected,
    consent,
  })
  return findings.length
}

/**
 * Keeping an answer's claims: choose, consent once, store.
 *
 * The consent moment is the *first saved finding*, not registration — because there is no
 * registration, and the first save is when the data would actually come into existence.
 * Declining is never a dead end: every refusal path here ends in the same download.
 */
function useCuration({
  question,
  prose,
  summary,
  account,
  onAccountChange,
}: {
  question: string
  prose: string
  summary: AnswerSummary
  account: Account | null
  onAccountChange: (account: Account | null) => void
}): Curation {
  const [asking, setAsking] = useState(false)
  const [curating, setCurating] = useState(false)
  const [kept, setKept] = useState<number[]>([])
  const [saved, setSaved] = useState(0)
  const [failure, setFailure] = useState<string | null>(null)

  function download() {
    downloadFinding(question, prose, summary)
  }

  async function store(consent: boolean, selected: number[]) {
    setFailure(null)
    try {
      setSaved(await keepClaims({ question, prose, summary, selected, consent }))
      setCurating(false)
      if (consent && account) {
        onAccountChange({ ...account, has_consented_to_findings: true })
      }
    } catch (error) {
      setFailure(error instanceof Error ? error.message : t('error.generic'))
    }
  }

  function begin() {
    if (!account) return download()
    // Curation before storage, not after: an answer makes several claims and
    // not all of them are worth a row. Keeping everything and pruning later
    // fills a corpus with claims the reader never thought were findings, and
    // the graph is only useful if every node was chosen.
    setKept(summary.claims.map((_, index) => index))
    setCurating(true)
  }

  function keep() {
    if (!account) return
    if (account.has_consented_to_findings) return void store(false, kept)
    setAsking(true)
  }

  function cancel() {
    setCurating(false)
  }

  function agree() {
    setAsking(false)
    void store(true, kept)
  }

  function decline() {
    setAsking(false)
    setCurating(false)
    download()
  }

  return { asking, curating, kept, setKept, saved, failure, download, begin, keep, cancel, agree, decline }
}

function SaveFinding(props: {
  question: string
  prose: string
  summary: AnswerSummary
  account: Account | null
  onAccountChange: (account: Account | null) => void
}) {
  const { summary, account } = props
  const curation = useCuration(props)
  const { asking, curating, kept, setKept, saved, failure } = curation

  return (
    <>
      <p>
        <button type="button" className="link" onClick={curation.begin}>
          {t('evidence.save')}
        </button>
        <span className="muted">
          {account
            ? saved > 0
              ? t('findings.savedCount', { count: saved })
              : ''
            : t('evidence.saveNote')}
        </span>
      </p>

      {curating && (
        <CurationPanel
          claims={summary.claims}
          kept={kept}
          onKept={setKept}
          onKeep={curation.keep}
          onCancel={curation.cancel}
          onDownload={curation.download}
        />
      )}

      {failure && (
        <p className="failure" role="alert">
          {failure}
        </p>
      )}
      {asking && <ConsentDialog onAgree={curation.agree} onDecline={curation.decline} />}
    </>
  )
}
